import { Answers, BikeAdapterProduct, FlowContext, FlowNode, NodeId } from './Types/Types.ts';
import { getThread, productsForAnswerPath } from './Utils/ProductFiltering.ts';

export const FLOW_START_NODE: NodeId = 'axle';
export const getFlowNode = (nodeId: NodeId): FlowNode => FLOW_NODES[nodeId];

const getConedLength = (row: BikeAdapterProduct): string => row.ThruAxleLengthConed.trim();
const getFlatLength = (row: BikeAdapterProduct): string => row.ThruAxleLengthFlat.trim();

const uniqueSorted = (values: string[]): string[] =>
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const hasConedAndFlatOption = (products: BikeAdapterProduct[], answers: Answers): boolean => {
  const filtered = productsForAnswerPath(products, answers);
  return filtered.some((product) => Boolean(getConedLength(product)) && Boolean(getFlatLength(product)));
};

const getYesNoOptions = (ctx: FlowContext) => [
  { value: 'yes', label: ctx.translations?.yes ?? '' },
  { value: 'no', label: ctx.translations?.no ?? '' },
];

export const FLOW_NODES: Record<NodeId, FlowNode> = {
  axle: {
    id: 'axle',
    kind: 'choice',
    answerKey: 'axleType',
    cmsParameter: 'axle',
    getNext: (_ctx, selected) => (selected === 'thru' ? 'selectThread' : 'horizontalDropout'),
  },
  horizontalDropout: {
    id: 'horizontalDropout',
    kind: 'choice',
    answerKey: 'horizontalDropout',
    cmsParameter: 'horizontalDropout',
    getOptions: (ctx) => getYesNoOptions(ctx),
    getNext: (ctx, selected) => {
      if (selected === 'yes') {
        return 'noFit';
      }
      return ctx.answers.axleType === 'solid' ? 'boltSize' : 'hoodedDropout';
    },
  },
  hoodedDropout: {
    id: 'hoodedDropout',
    kind: 'choice',
    answerKey: 'hoodedDropout',
    cmsParameter: 'hoodedDropout',
    getOptions: (ctx) => getYesNoOptions(ctx),
    getNext: (_ctx, selected) => (selected === 'yes' ? 'buyAdapter' : 'includedAdapter'),
  },
  boltSize: {
    id: 'boltSize',
    kind: 'choice',
    answerKey: 'boltSize',
    cmsParameter: 'boltSize',
    getOptions: (ctx) => getYesNoOptions(ctx),
    getNext: (_ctx, selected) => (selected === 'yes' ? 'noAdapterNeeded' : 'selectThread'),
  },
  selectThread: {
    id: 'selectThread',
    kind: 'choice',
    answerKey: 'thread',
    cmsParameter: 'selectThread',
    getOptions: (ctx) => {
      const filtered = productsForAnswerPath(ctx.products, ctx.answers);
      return uniqueSorted(filtered.map((product) => getThread(product))).map((thread) => ({
        value: thread,
        label: thread,
      }));
    },
    getNext: (ctx) => {
      if (ctx.answers.axleType !== 'thru') {
        return 'buyAdapter';
      }
      return hasConedAndFlatOption(ctx.products, ctx.answers) ? 'conedOrFlat' : 'selectLength';
    },
  },
  conedOrFlat: {
    id: 'conedOrFlat',
    kind: 'choice',
    answerKey: 'coneType',
    cmsParameter: 'conedOrFlat',
    getOptions: (ctx) => [
      { value: 'flat', label: ctx.translations?.flat ?? '' },
      { value: 'coned', label: ctx.translations?.coned ?? '' },
    ],
    getNext: () => 'selectLength',
  },
  selectLength: {
    id: 'selectLength',
    kind: 'choice',
    answerKey: 'length',
    cmsParameter: 'selectLength',
    getOptions: (ctx) => {
      const filtered = productsForAnswerPath(ctx.products, ctx.answers);
      const lengths =
        ctx.answers.coneType === 'coned'
          ? filtered.map((product) => getConedLength(product))
          : filtered.map((product) => getFlatLength(product));
      return uniqueSorted(lengths).map((length) => ({ value: length, label: length }));
    },
    getNext: () => 'buyAdapter',
  },
  noFit: {
    id: 'noFit',
    kind: 'result',
    cmsParameter: 'noFit',
  },
  noAdapterNeeded: {
    id: 'noAdapterNeeded',
    kind: 'result',
    cmsParameter: 'noAdapterNeeded',
  },
  includedAdapter: {
    id: 'includedAdapter',
    kind: 'result',
    cmsParameter: 'includedAdapter',
  },
  buyAdapter: {
    id: 'buyAdapter',
    kind: 'result',
    cmsParameter: 'buyAdapter',
  },
};
